import {KeyboardEvent, useCallback, useEffect, useRef, useState} from 'react';
import {fenzsafeStore, isFenzSafeAdapterReal} from './fenzsafeStore';
import {
  FenzSafeSummaryDraftError,
  isValidSummaryScope,
  requestSummaryDraft,
  summaryCopyText,
  summaryErrorMessage,
  SUMMARY_UNAVAILABLE_CODE,
  SUMMARY_UNKNOWN_CODE,
  type SummaryDraftTarget,
  type ValidatedSummaryDraft,
} from './fenzsafeSummaryDraft';
import './FenzSafeSummaryDraftPanel.css';

/**
 * Issue #977 summary slice 2: request, read and dismiss ONE unverified AI summary draft of one
 * accepted document. The parent renders this only when the server's fenzsafe_matter_summary_ready
 * capability is true, and must key it by account scope, matter, document and version.
 *
 * Request identity: the target (scope, matter, document, version, hash) is captured when the owner
 * opens the confirmation and is never updated. Any store notification that no longer shows that
 * exact accepted, non-superseded document -- including the intervening empty snapshot of a backend
 * reset -- bumps a monotonic generation, aborts the request and clears all content synchronously,
 * so a late response cannot repopulate this or any newer panel.
 */

type Props = {
  /** Authenticated `organization:identity` scope from FenzSafeWorkspace; never inferred. */
  scope: string | undefined;
  /** Live server capability; false clears everything. */
  enabled: boolean;
  matterId: string;
  documentId: string;
  documentName: string;
  docVersion: number;
  contentSha256: string | undefined;
  disabled?: boolean;
  /** Test seam only. */
  fetchImpl?: typeof fetch;
};

type View =
  | {kind: 'closed'}
  | {kind: 'confirm'; target: SummaryDraftTarget}
  | {kind: 'sending'; target: SummaryDraftTarget}
  | {kind: 'draft'; target: SummaryDraftTarget; draft: ValidatedSummaryDraft}
  | {kind: 'error'; target: SummaryDraftTarget; message: string};

export const SUMMARY_PDF_NAME = /\.pdf$/i;

/** Filename eligibility is only a UI hint; the server's actual-byte check (415) is authoritative. */
export function isSummaryEligibleName(name: string): boolean {
  return SUMMARY_PDF_NAME.test(name.trim());
}

function liveDocumentMatches(target: SummaryDraftTarget): boolean {
  if (!isFenzSafeAdapterReal()) return false;
  const matter = fenzsafeStore.getSnapshot().matters.find(item => item.id === target.matterId);
  const document = matter?.documents.find(item => item.id === target.documentId);
  return document !== undefined && document.processingStatus === 'accepted' && !document.supersededBy
    && document.docVersion === target.docVersion && document.contentSha256 === target.contentSha256;
}

export function FenzSafeSummaryDraftControl({scope, enabled, matterId, documentId, documentName, docVersion, contentSha256, disabled, fetchImpl}: Props) {
  const [view, setView] = useState<View>({kind: 'closed'});
  const [copyStatus, setCopyStatus] = useState('');
  const generation = useRef(0);
  const busy = useRef(false);
  const inFlight = useRef<AbortController | null>(null);
  const activeTarget = useRef<SummaryDraftTarget | null>(null);
  const mounted = useRef(true);
  const opener = useRef<HTMLButtonElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  // The opener is disabled while the panel is open, so focus returns only after it re-renders enabled.
  const returnFocus = useRef(false);
  // Live values for rechecks after an await; a render-time closure would be stale.
  const live = useRef({enabled, scope});
  live.current = {enabled, scope};

  const invalidate = useCallback((shouldReturnFocus: boolean) => {
    generation.current += 1;
    inFlight.current?.abort();
    inFlight.current = null;
    busy.current = false;
    activeTarget.current = null;
    if (mounted.current) {
      setView({kind: 'closed'});
      setCopyStatus('');
      returnFocus.current = shouldReturnFocus;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
      inFlight.current?.abort();
      inFlight.current = null;
      busy.current = false;
      activeTarget.current = null;
    };
  }, []);

  // Synchronous invalidation from the store itself, so an empty reset snapshot followed by a
  // same-document reload cannot be collapsed away by batched rendering.
  useEffect(() => fenzsafeStore.subscribe(() => {
    const target = activeTarget.current;
    if (target !== null && !liveDocumentMatches(target)) invalidate(false);
  }), [invalidate]);

  useEffect(() => {
    if (!enabled && activeTarget.current !== null) invalidate(false);
  }, [enabled, invalidate]);

  useEffect(() => {
    if (view.kind !== 'closed') heading.current?.focus();
    else if (returnFocus.current) { returnFocus.current = false; opener.current?.focus(); }
  }, [view.kind]);

  if (!isSummaryEligibleName(documentName)) return null;

  const open = () => {
    if (busy.current || !enabled || contentSha256 === undefined || !isValidSummaryScope(scope)) return;
    const target: SummaryDraftTarget = Object.freeze({scope, matterId, documentId, docVersion, contentSha256});
    if (!liveDocumentMatches(target)) return;
    generation.current += 1;
    activeTarget.current = target;
    setCopyStatus('');
    setView({kind: 'confirm', target});
  };

  const send = async (target: SummaryDraftTarget) => {
    // Synchronous guard: set before any await, so a double click or repeated Enter sends once.
    if (busy.current || activeTarget.current !== target) return;
    if (!live.current.enabled || target.scope !== live.current.scope || !liveDocumentMatches(target)) { invalidate(false); return; }
    busy.current = true;
    const myGeneration = generation.current;
    const controller = new AbortController();
    inFlight.current = controller;
    setView({kind: 'sending', target});
    const current = () => mounted.current && generation.current === myGeneration && activeTarget.current === target;
    try {
      const draft = await requestSummaryDraft(target, {adapterReal: isFenzSafeAdapterReal(), signal: controller.signal, fetchImpl});
      if (!current()) return;
      if (!live.current.enabled || target.scope !== live.current.scope || !liveDocumentMatches(target)) { invalidate(false); return; }
      setView({kind: 'draft', target, draft});
    } catch (error) {
      if (!current()) return;
      const code = error instanceof FenzSafeSummaryDraftError ? error.code : (controller.signal.aborted ? SUMMARY_UNAVAILABLE_CODE : SUMMARY_UNKNOWN_CODE);
      setView({kind: 'error', target, message: summaryErrorMessage(code)});
    } finally {
      if (generation.current === myGeneration) {
        busy.current = false;
        inFlight.current = null;
      }
    }
  };

  const copy = async (draft: ValidatedSummaryDraft) => {
    try {
      await navigator.clipboard.writeText(summaryCopyText(documentName, draft));
      if (mounted.current && activeTarget.current !== null) setCopyStatus('Copied as plain text, with the unverified labels and the notice.');
    } catch {
      if (mounted.current && activeTarget.current !== null) setCopyStatus('Copy failed — select the text instead.');
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') { event.preventDefault(); invalidate(true); }
  };

  const unavailable = !enabled || contentSha256 === undefined || !isValidSummaryScope(scope);

  return <>
    <button type="button" ref={opener} className="fsa-secondary" disabled={disabled || unavailable || view.kind !== 'closed'}
      onClick={open} aria-label={`Draft an AI summary (unverified) of ${documentName}`}>
      Draft an AI summary (unverified)
    </button>
    {view.kind !== 'closed' && (
      <section className="fssd-panel" role="dialog" aria-labelledby={`fssd-title-${documentId}`} onKeyDown={onKeyDown}>
        <h3 id={`fssd-title-${documentId}`} ref={heading} tabIndex={-1}>AI summary draft — unverified</h3>
        {view.kind === 'confirm' && <>
          <p>Summaries can be requested by the person who created the matter.</p>
          <p>Before you continue, please confirm what happens with <strong>{documentName}</strong>:</p>
          <ul>
            <li>This one document is sent to Google&apos;s Vertex AI service to be read.</li>
            <li>It uses one of your monthly summary readings. A failed attempt may still use a reading.</li>
            <li>The summary is not saved. It is an unverified draft, not legal advice.</li>
          </ul>
          <div className="fssd-actions">
            <button type="button" className="fsa-primary" onClick={() => void send(view.target)}>Send and draft</button>
            <button type="button" className="fsa-secondary" onClick={() => invalidate(true)}>Cancel</button>
          </div>
        </>}
        {view.kind === 'sending' && <>
          <p role="status">Preparing the draft. Closing this panel does not cancel a reading already sent.</p>
          <div className="fssd-actions"><button type="button" className="fsa-secondary" onClick={() => invalidate(true)}>Close</button></div>
        </>}
        {view.kind === 'error' && <>
          <p role="alert">{view.message}</p>
          <p>Any new attempt needs a fresh confirmation and may use another reading.</p>
          <div className="fssd-actions"><button type="button" className="fsa-secondary" onClick={() => invalidate(true)}>Close</button></div>
        </>}
        {view.kind === 'draft' && <>
          <p className="fssd-notice" role="note">{view.draft.notice}</p>
          <p className="fssd-source">Source: {documentName} · version {view.draft.source.docVersion} · SHA-256 {view.draft.source.contentSha256.slice(0, 12)}…</p>
          <p className="fssd-unsaved"><strong>This summary is not saved.</strong> A record that a reading happened (no content) is kept for your account&apos;s audit.</p>
          {view.draft.statements.length === 0 ? <p>No statements were returned.</p> : (
            <ol className="fssd-statements">
              {view.draft.statements.map((item, index) => (
                <li key={index}>
                  <p>{item.statement}</p>
                  <p className="fssd-citation"><span className="fssd-label">Unverified quote — check page {item.page}:</span> <q>{item.excerpt}</q></p>
                </li>
              ))}
            </ol>
          )}
          {view.draft.warnings.length > 0 && <>
            <h4>Reading notes (unverified)</h4>
            <ul>{view.draft.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
          </>}
          <div className="fssd-actions">
            <button type="button" className="fsa-secondary" onClick={() => void copy(view.draft)}>Copy as plain text</button>
            <button type="button" className="fsa-secondary" onClick={() => invalidate(true)}>Close and discard</button>
          </div>
          {copyStatus && <p role="status">{copyStatus}</p>}
          <p className="fssd-small">Copying does not save the summary here, and closing cannot remove text you already copied.</p>
        </>}
      </section>
    )}
  </>;
}
